import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { base44 } from '@/api/base44Client';
import { BUSINESS } from '@/lib/businessConfig';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import Button from '@/components/atelier/Button';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import SectionHeader from '@/components/atelier/SectionHeader';
import ScrollMarquee from '@/components/atelier/ScrollMarquee';
import ServiceCard from '@/components/atelier/ServiceCard';
import BookingTier from '@/components/atelier/BookingTier';
import InnovationCard from '@/components/atelier/InnovationCard';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import BeforeAfterCard from '@/components/atelier/BeforeAfterCard';
import LazyOnView from '@/components/LazyOnView';
const TestimonialsLoop = lazy(() => import('@/components/atelier/TestimonialsLoop'));
import JournalCard from '@/components/atelier/JournalCard';
import AreaPills from '@/components/atelier/AreaPills';
import FAQAccordion from '@/components/atelier/FAQAccordion';
// Hero is extracted to its own module — minimal imports, renders immediately
import HeroSection from '@/components/atelier/HeroSection';
import FindUsSection from '@/components/atelier/FindUsSection';
import { HOME_FAQ } from '@/lib/homeFaq';
import HomeSEOSections from '@/components/atelier/HomeSEOSections';
const InstagramGrid = lazy(() => import('@/components/atelier/InstagramGrid'));

const BOOKING_URL = 'https://www.fresha.com/a/ikonomakis-hair-atelier-halandri-irakleitoy-80-bxynmijf?pId=770996';

const MARQUEE_ITEMS = [
  { label: 'Bespoke Color' },
  { label: 'Precision Cutting', gold: true },
  { label: 'AI Hair Analysis' },
  { label: 'Bridal Hair', gold: true },
  { label: 'Premium Treatments' },
  { label: 'Total Look', gold: true },
];

const FEATURED_SERVICES = [
  {
    spanClass: 'lg:col-span-7',
    variant: 'large',
    image: 'warmBrown',
    imageUrl: 'https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/2ab6be249_IMG_8421.jpg',
    imageAlt: 'Balayage αποτέλεσμα στο Ikonomakis Hair Atelier',
    tags: [{ label: '✦ Signature' }, { label: 'Χρώμα' }],
    title: 'Balayage',
    description: 'Hand-painted χρώμα με φυσικές μεταβάσεις. Soft sun-kissed effect που μεγαλώνει χάρη με το χρόνο.',
    priceFrom: 150,
    href: '/balayage-chalandri',
  },
  {
    spanClass: 'lg:col-span-5',
    variant: 'dark',
    image: 'deepBrown',
    imageUrl: 'https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/da7a175c8_Image17.jpeg',
    imageAlt: 'Hair Color custom χρωματική παλέτα στο Ikonomakis Hair Atelier',
    tags: [{ label: '✦ Bespoke' }, { label: 'Χρώμα' }],
    title: 'Hair Color',
    description: 'Custom χρωματικές παλέτες. Από rich brunette σε editorial blonde — σχεδιασμένο για τη σιλουέτα σου.',
    priceFrom: 43,
    href: '/hair-color-chalandri',
  },
  {
    spanClass: 'lg:col-span-12',
    variant: 'full',
    image: 'goldRich',
    tags: [{ label: '✦ Premium' }, { label: 'Θεραπεία' }],
    title: 'Keratin Nanocure',
    description: 'Premium ίσιωμα με flagship τεχνολογία. Φυσικό αποτέλεσμα 4-6 μηνών, με προαιρετική Botox ενίσχυση.',
    priceFrom: 150,
    href: '/keratin-treatment-chalandri',
  },
  {
    spanClass: 'lg:col-span-6',
    variant: 'med-ivory',
    image: 'goldSoft',
    tags: [{ label: '✦ Master Expertise' }, { label: 'Χρώμα' }],
    title: 'Blonde Specialist',
    description: 'Η τέχνη του premium ξάνθου. Από soft blonde balayage μέχρι editorial bright tones, με master expertise.',
    priceFrom: 150,
    href: '/blonde-specialist-chalandri',
  },
  {
    spanClass: 'lg:col-span-6',
    variant: 'med-cream',
    image: 'goldGlow',
    tags: [{ label: 'Θεραπεία' }, { label: 'Molecular Repair' }],
    title: 'K18 Hair Treatment',
    description: 'Molecular peptide repair σε 4 λεπτά. Αναδόμηση για χημικά επιβαρυμένα μαλλιά — επιστημονική προσέγγιση.',
    priceFrom: 45,
    href: '/k18-hair-treatment-chalandri',
  },
  {
    spanClass: 'lg:col-span-12',
    variant: 'full',
    image: 'goldGlow',
    tags: [{ label: '✦ Japanese Tech' }, { label: 'Θεραπεία' }],
    title: 'Milbon 360°',
    description: 'Ιαπωνική θεραπεία νανοτεχνολογίας 360°. Βαθιά αναδόμηση, όγκος και λάμψη που κρατούν — με ολοκληρωμένο πρωτόκολλο salon & homecare.',
    priceFrom: 195,
    href: '/milbon-360-chalandri',
  },
];

const BOOKING_TIERS = [
  {
    level: 'i',
    name: 'Hair Stylist / Colorist',
    description: 'Άρτιοι επαγγελματίες με σταθερή ποιότητα. Ιδανικό για classics και maintenance.',
    priceFrom: 45,
    href: BOOKING_URL,
  },
  {
    level: 'ii',
    name: 'Hair Expert / Color Expert',
    description: 'Σύμβουλοι ομορφιάς με βαθιά τεχνογνωσία. Για evolved looks και color transformations.',
    priceFrom: 65,
    href: BOOKING_URL,
  },
  {
    level: 'iii',
    name: 'Master Stylist / Master Color Specialist',
    description: 'Οι πιο έμπειροι ειδικοί του atelier. Bespoke, signature, editorial-level work.',
    priceFrom: 90,
    href: BOOKING_URL,
    featured: true,
  },
];

const BA_FALLBACK_BEFORE = 'linear-gradient(135deg, #2a221b, #4a3d31)';
const BA_FALLBACK_AFTER = 'linear-gradient(135deg, #d4b88a, #c4a87c)';

const toCardProps = (ba) => ({
  service: ba.service_category,
  title: ba.title,
  description: ba.caption,
  beforeImage: ba.before_image_url ? `url(${ba.before_image_url}) center/cover` : BA_FALLBACK_BEFORE,
  afterImage: ba.after_image_url ? `url(${ba.after_image_url}) center/cover` : BA_FALLBACK_AFTER,
  href: '/before-after',
});

const TESTIMONIALS = [
  {
    quote: 'Η εμπειρία μου ήταν καταπληκτική όπως πάντα, εξυπηρέτηση, άνεση και ένα τέλειο κούρεμα.',
    authorName: 'Αδριάνα Μ',
    authorRole: '5/5 · 29 Μαΐου 2026',
    authorInitial: 'Α',
    authorGradient: 'linear-gradient(135deg, #c4a87c, #5D4A3B)',
  },
  {
    quote: 'Καταπληκτική δουλειά! Οπως πάντα!',
    authorName: 'Χριστίνα Μ.',
    authorRole: '5/5 · 4 Μαρ 2026',
    authorInitial: 'Χ',
    authorGradient: 'linear-gradient(135deg, #ede2cc, #a98a5f)',
  },
  {
    quote: 'Διόρθωσε το προηγούμενο μου χρώμα άψογα, πετυχαίνοντας φυσικό αποτέλεσμα.',
    authorName: 'Evrydiki K.',
    authorRole: '5/5 · 17 Απρ 2026',
    authorInitial: 'E',
    authorGradient: 'linear-gradient(135deg, #5D4A3B, #1C1814)',
  },
  {
    quote: 'Ευχάριστη ατμόσφαιρα, άψογος επαγγελματισμός και υπέροχο αποτέλεσμα! :)',
    authorName: 'Anastasia P.',
    authorRole: '5/5 · 16 Απρ 2026',
    authorInitial: 'A',
    authorGradient: 'linear-gradient(135deg, #d4b88a, #8a6f48)',
  },
  {
    quote: 'Τέλειο χρώμα και κούρεμα, άψογοι επαγγελματίες όλοι όσοι εργάστηκαν για αυτό που ήθελα να αλλαξει στα μαλλιά μου.',
    authorName: 'Quan D.',
    authorRole: '5/5 · 24 Απρ 2026',
    authorInitial: 'Q',
    authorGradient: 'linear-gradient(135deg, #a98a5f, #5D4A3B)',
  },
  {
    quote: 'Άψογη εξυπηρέτηση! Πάντα πολύ ικανοποιημένη με το αποτέλεσμα.',
    authorName: 'Xenia K.',
    authorRole: '5/5 · 2026',
    authorInitial: 'X',
    authorGradient: 'linear-gradient(135deg, #ede2cc, #c4a87c)',
  },
  {
    quote: 'Πάντα άψογη εξυπηρέτηση. Η Ευγενία είναι άψογη επαγγελματίας.',
    authorName: 'Fresha guest',
    authorRole: '5/5 · 2026',
    authorInitial: '5',
    authorGradient: 'linear-gradient(135deg, #5D4A3B, #c4a87c)',
  },
  {
    quote: 'Εξαιρετική εμπειρία. Μου προτάθηκαν ανταύγειες σε ανοιχτότερο χρώμα και το αποτέλεσμα με ικανοποίησε απόλυτα.',
    authorName: 'Fresha guest',
    authorRole: '5/5 · 2026',
    authorInitial: '5',
    authorGradient: 'linear-gradient(135deg, #f1e7d6, #a98a5f)',
  },
  {
    quote: 'Το αποτέλεσμα ήταν ακριβώς αυτό που ήθελα! Άψογοι επαγγελματίες!',
    authorName: 'ΜΑΡΙΕΤΤΑ Κ.',
    authorRole: '5/5 · 7 Μαΐου 2026',
    authorInitial: 'Μ',
    authorGradient: 'linear-gradient(135deg, #1C1814, #a98a5f)',
  },
];

const JOURNAL = [
  {
    category: 'Color Guide',
    title: 'Οι 5 παρανοήσεις για το balayage',
    excerpt: 'Τι είναι πραγματικά το balayage, πώς διαφέρει από τις μέθοδες highlights, και τι να περιμένεις στην πρώτη σου επίσκεψη.',
    date: '15 Μαΐου 2026',
    readTime: '6 λεπτά',
    image: 'linear-gradient(165deg, #c4a87c, #5D4A3B)',
    href: '/balayage-paranoiseis',
  },
  {
    category: 'Care Tips',
    title: 'Πώς να διατηρήσεις το χρώμα σου μετά τη βαφή',
    excerpt: 'Από το shampoo και τους τρόπους styling, ως τα professional treatments. Maintenance plan για κάθε χρωματικό αποτέλεσμα.',
    date: '8 Μαΐου 2026',
    readTime: '5 λεπτά',
    image: 'linear-gradient(165deg, #d4b88a, #a98a5f)',
    href: '/pos-na-diatirisis-to-xroma-sou',
  },
  {
    category: 'Atelier News',
    title: 'Η νέα εποχή του premium hairdressing στην Αθήνα',
    excerpt: 'Πώς το Ikonomakis Hair Atelier συνδυάζει 30 χρόνια εμπειρίας με AI-driven τεχνολογία και bespoke χρώμα.',
    date: '1 Μαΐου 2026',
    readTime: '8 λεπτά',
    image: 'linear-gradient(165deg, #3a2e22, #c4a87c)',
    href: '/neo-epochi-premium-hairdressing-athina',
  },
];

const AREAS = [
  'Χαλάνδρι', 'Φιλοθέη', 'Ψυχικό', 'Κηφισιά', 'Μαρούσι',
  'Π. Πεντέλη', 'Αγία Παρασκευή', 'Χολαργός', 'Παπάγου',
  'Νέο Ψυχικό', 'Νέα Ερυθραία', 'Εκάλη',
];

const FAQ_ITEMS = HOME_FAQ;

export default function HomeAtelier() {
  const [beforeAfters, setBeforeAfters] = useState([]);
  const [landingImages, setLandingImages] = useState({});
  const hasFetched = useRef(false);

  useEffect(() => {
    // useRef guard prevents React StrictMode double-fetch
    if (hasFetched.current) return;
    hasFetched.current = true;

    const load = () => {
      if (hasFetched.current !== true) return; // safety check
      base44.functions.invoke('getPublicBeforeAfters', {}).then((res) => {
        setBeforeAfters(res.data?.items || []);
      });
      base44.functions.invoke('getPublicLandingPages', { lang: 'el' }).then((res) => {
        const map = {};
        (res.data?.pages || []).forEach((p) => {
          if (p.lang !== 'en' && p.slug && p.hero_image_url) map[p.slug] = p.hero_image_url;
        });
        setLandingImages(map);
      });
    };

    // Fire ONLY after the page is fully loaded and interactive — well past LCP window
    if (document.readyState === 'complete') {
      const id = setTimeout(load, 3000);
      return () => clearTimeout(id);
    } else {
      const onLoad = () => { setTimeout(load, 3000); };
      window.addEventListener('load', onLoad, { once: true });
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  return (
    <div className="bg-cream-bg text-warm-black">
      <TopBar />
      <Nav />
      <main id="main-content">
      {/* 1. HERO — extracted to HeroSection, renders immediately with zero data deps */}
      <HeroSection />
      
      {/* 1b. ABOUT — brief intro section, immediately below hero for word count & crawlability */}
      <section aria-labelledby="about-intro-heading" className="px-[6vw] py-14 bg-cream border-b border-brown/10">
        <div className="max-w-reading mx-auto">
          <h2 id="about-intro-heading" className="font-serif text-h2 text-warm-black font-medium mb-4">Σχετικά με εμάς</h2>
          <p className="text-body-l text-muted leading-[1.7] mb-3">
            Το Ikonomakis Hair Atelier είναι premium κομμωτήριο στο Χαλάνδρι με <strong className="text-warm-black">30+ χρόνια εμπειρίας</strong> και <strong className="text-warm-black">2η γενιά κομμωτών</strong>. Εξειδικευόμαστε σε bespoke balayage και precision cutting — κάθε υπηρεσία σχεδιάζεται αποκλειστικά για το σχήμα του προσώπου και το lifestyle κάθε πελάτη.
          </p>
          <p className="text-body text-muted leading-[1.7] mb-3">
            Ο ιδρυτής Θοδωρής Οικονομάκης έχει εκπαιδευτεί και εκπαιδεύσει επαγγελματίες κομμωτές σε <strong className="text-warm-black">19 χώρες</strong>, και είναι δημιουργός του <strong className="text-warm-black">Kommosis AI</strong> και του <strong className="text-warm-black">Face Metrics Pro</strong> — των πρώτων AI εργαλείων για κομμωτήρια στην Ελλάδα.
          </p>
          <p className="text-body text-muted leading-[1.7]">
            Εξυπηρετούμε πελάτες από Χαλάνδρι, Φιλοθέη, Ψυχικό, Κηφισιά, Μαρούσι και ολόκληρη την Αθήνα — αξιολόγηση <strong className="text-warm-black">{BUSINESS.reviews.rating}/5 από {BUSINESS.reviews.display} κριτικές</strong> στο Fresha.
          </p>
        </div>
      </section>

      {/* SEO SECTIONS — Χαλάνδρι about, color comparison, reviews, Face Metrics, price list */}
      <HomeSEOSections />

      {/* 2. AWARDS BAR */}
      <div className="bg-cream-soft border-b border-brown/10 px-[6vw] py-8">
        <div className="max-w-container mx-auto">
          <div className="text-center mb-6">
            <span className="text-eyebrow uppercase tracking-widest text-brown/60">Διακρίσεις & Ορόσημα</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: '🏆', title: '3 Διακρίσεις Καινοτομίας', subtitle: 'Hellenic Innovation Forum & Awards', year: '2021' },
              { icon: '🤖', title: 'Face Metrics Pro', subtitle: 'Πρώτη AI ανάλυση προσώπου σε κομμωτήριο στην Ελλάδα', year: '2024' },
              { icon: '🎓', title: 'Hair Community Greece', subtitle: 'Ιδρυτής — εκπαιδευτική πλατφόρμα κομμωτών', year: '2019' },
              { icon: '🌍', title: '19 Χώρες', subtitle: 'Εκπαίδευση & διδασκαλία επαγγελματιών κομμωτών', year: '30+ έτη' },
            ].map((award) => (
              <div key={award.title} className="flex items-start gap-3 p-4 rounded-card bg-warm-black/[0.03] border border-brown/10">
                <span className="text-2xl shrink-0">{award.icon}</span>
                <div>
                  <div className="text-tiny font-bold text-warm-black uppercase tracking-wide leading-tight">{award.title}</div>
                  <div className="text-tiny text-muted mt-0.5 leading-snug">{award.subtitle}</div>
                  <div className="text-tiny text-gold font-semibold mt-1">{award.year}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3. BRAND TRUST BAR */}
      <div className="bg-warm-black border-b border-gold-light/10 px-[6vw] py-5">
        <div className="max-w-container mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            <span className="text-eyebrow uppercase text-gold-light tracking-widest shrink-0">Χρησιμοποιούμε</span>
            {['Milbon', 'Moroccanoil', 'L\'Oréal Professionnel', 'Kérastase', 'K18', 'Schwarzkopf'].map((brand) => (
              <span key={brand} className="text-tiny font-semibold tracking-[1.5px] uppercase text-cream/70 hover:text-gold-light transition-colors">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* 3. MARQUEE */}
      <ScrollMarquee items={MARQUEE_ITEMS} />
      
      {/* 3. PHILOSOPHY TEASER */}
      <section aria-labelledby="philosophy-heading" className="cv-auto px-[6vw] py-[120px]">
        <div className="max-w-editorial mx-auto text-center">
          <EyebrowPill variant="dark" className="mb-7">
            Φιλοσοφία
          </EyebrowPill>
          <h2 id="philosophy-heading" className="font-serif text-display-m text-warm-black font-medium leading-[1] tracking-[-2px] mb-7">
            The Atelier <em className="italic text-gold">Philosophy</em>
          </h2>
          <div className="text-body-l text-muted leading-[1.7] max-w-[760px] mx-auto space-y-6">
            <p>
              Πίσω από το Ikonomakis Hair Atelier υπάρχει μια διαδρομή τριών δεκαετιών στην κομμωτική τέχνη, χτισμένη πάνω στην εμπειρία, τη συνεχή εκπαίδευση και την ανάγκη για εξέλιξη.
            </p>
            <p>
              Ο Θοδωρής Οικονομάκης, δεύτερη γενιά κομμωτής, μεγάλωσε μέσα σε μια οικογένεια με έντονη παρουσία στον κλάδο της κομμωτικής. Αυτές οι ρίζες δημιούργησαν μια σταθερή βάση: σεβασμός στην τέχνη, συνέπεια στην τεχνική και προσωπική ευθύνη απέναντι σε κάθε πελάτη.
            </p>
            <p>
              Σήμερα, μέσα από το Ikonomakis Hair Atelier, το Hair Community Greece και το Face Metrics Pro, αυτή η διαδρομή συνεχίζεται με μια πιο σύγχρονη προσέγγιση: η κομμωτική δεν βασίζεται μόνο στο ένστικτο, αλλά στη διάγνωση, στην εμπειρία και στην τεχνολογία.
            </p>
            <p className="font-serif italic text-[24px] text-warm-black leading-[1.45]">
              Η πραγματική πολυτέλεια δεν χρειάζεται να φωνάζει.<br />
              Φαίνεται στον τρόπο που σε ακούν, σε κατανοούν και σχεδιάζουν κάτι πραγματικά δικό σου.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-card-sm border border-gold/30 bg-cream-soft">
              <span className="text-gold text-lg">✦</span>
              <p className="text-body-s text-warm-black font-medium leading-snug text-left">
                Δημιουργοί του <span className="text-gold font-semibold">Kommosis AI</span>, του <span className="text-gold font-semibold">Face Metrics Pro</span> και του <span className="text-gold font-semibold">AI Hair Diagnosis</span> — τα πρώτα εργαλεία AI για κομμωτήρια στην Ελλάδα.
              </p>
            </div>
          </div>
        </div>

        {/* The Ikonomakis Method — Branded 5-step methodology */}
        <div className="max-w-container-narrow mx-auto mt-20">
          <div className="text-center mb-12">
            <EyebrowPill variant="gold-soft" className="mb-4">The Ikonomakis Method</EyebrowPill>
            <h3 className="font-serif text-h1 text-warm-black font-medium">Διάγνωση πριν από κάθε υπηρεσία</h3>
            <p className="text-body text-muted mt-3 max-w-[560px] mx-auto">Κάθε επίσκεψη ξεκινά με επαγγελματική αξιολόγηση — ποτέ δεν αρχίζουμε χωρίς να καταλάβουμε τι χρειάζεσαι.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { num: '01', label: 'Διάγνωση', desc: 'Ανάλυση μαλλιών & σχήματος προσώπου' },
              { num: '02', label: 'Πρόταση', desc: 'Bespoke σχέδιο χρώματος & κουρέματος' },
              { num: '03', label: 'Εφαρμογή', desc: 'Εκτέλεση από τον κατάλληλο ειδικό' },
              { num: '04', label: 'Αξιολόγηση', desc: 'Έλεγχος αποτελέσματος μαζί σου' },
              { num: '05', label: 'Maintenance', desc: 'Personalized πλάνο συντήρησης' },
            ].map((step) => (
              <div key={step.num} className="flex flex-col items-center text-center p-5 rounded-card bg-cream-soft border border-brown/10">
                <div className="font-serif text-[32px] text-gold font-medium leading-none mb-2">{step.num}</div>
                <div className="font-semibold text-warm-black text-body-s mb-1">{step.label}</div>
                <div className="text-tiny text-muted leading-snug">{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* 4. FEATURED SERVICES */}
      <section aria-labelledby="services-heading" className="cv-auto px-[6vw] pb-[100px]">
        <div className="max-w-container mx-auto">
          <SectionHeader
            id="services-heading"
            eyebrow="Signature Υπηρεσίες"
            title="Η τέχνη μας,"
            titleEm="με την υπογραφή σου."
            subtitle="Bespoke χρώμα, precision cutting, premium θεραπείες. Σχεδιασμένες για τη σιλουέτα και το lifestyle σου."
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {FEATURED_SERVICES.map((s, i) => {
              const { spanClass, ...cardProps } = s;
              const slug = (cardProps.href || '').replace(/^\//, '');
              const imageUrl = cardProps.imageUrl || landingImages[slug];
              return (
                <div key={i} className={spanClass}>
                  <ServiceCard {...cardProps} imageUrl={imageUrl} />
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* 5. INNOVATION */}
      <section className="cv-auto px-[6vw] py-[100px]">
        <InnovationCard
          eyebrow="Καινοτομία · Πρώτο στην Ελλάδα"
          title="Face Metrics Pro"
          titleEm="AI Hair Analysis"
          description="Επιστημονική αξιολόγηση της σιλουέτας του προσώπου και της κατάστασης των μαλλιών για bespoke recommendations χρώματος και κουρέματος. Διαθέσιμο σε Online και Atelier format."
          ctaText="Ανακαλύψτε την Καινοτομία"
          ctaHref="/services/face-metrics-pro"
        />
      </section>
      
      {/* 6. BOOKING TIERS */}
      <section aria-labelledby="tiers-heading" className="cv-auto bg-warm-black text-cream px-[6vw] py-[120px]">
        <div className="max-w-container-narrow mx-auto">
          <SectionHeader
            variant="light"
            eyebrow="3 Tiers · Διάλεξε τον δικό σου ειδικό"
            title="Κάθε επίπεδο,"
            titleEm="με τη δική του υπογραφή."
            subtitle="Από τους έμπειρους Hair Stylists μέχρι τους Master Color Specialists. Όλοι μοιραζόμαστε την ίδια premium φιλοσοφία και μεθοδολογία."
          />
          
          <div className="space-y-4">
            {BOOKING_TIERS.map((tier, i) => (
              <BookingTier key={i} {...tier} />
            ))}
          </div>
        </div>
      </section>
      
      {/* 7. BEFORE / AFTER */}
      <section aria-labelledby="transformations-heading" className="cv-auto px-[6vw] py-[120px]">
        <div className="max-w-container mx-auto">
          <SectionHeader
            id="transformations-heading"
            eyebrow="Transformations"
            title="Δες τη δουλειά μας,"
            titleEm="πριν και μετά."
            subtitle="Πραγματικά αποτελέσματα από το atelier μας. Κάθε transformation είναι αποτέλεσμα consultation, σχεδιασμού και εξειδικευμένης εφαρμογής."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {beforeAfters.map((ba) => (
              <BeforeAfterCard key={ba.id} {...toCardProps(ba)} />
            ))}
          </div>
        </div>
      </section>
      
      {/* 8. REVIEWS */}
      <section id="reviews" aria-labelledby="reviews-heading" className="cv-auto bg-cream-soft px-[6vw] py-[120px] scroll-mt-28">
        <div className="max-w-container mx-auto">
          <div className="relative mb-12 pr-0 lg:pr-[260px]">
            <div className="absolute right-0 top-0 hidden lg:block text-right">
            <div className="font-serif text-display-s text-warm-black font-medium leading-none tracking-[-1.5px]">
              {BUSINESS.reviews.display}
            </div>
              <div className="mt-2 text-eyebrow uppercase text-brown/70 font-medium">
                Πιστοποιημένες αξιολογήσεις
              </div>
            </div>

            <h2 id="reviews-heading" className="sr-only">Αξιολογήσεις πελατών</h2>
            <EyebrowPill variant="dark" className="mb-6">
              Οι επισκέπτες μας
            </EyebrowPill>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
              <div className="flex items-center gap-1 text-[34px] leading-none" aria-label="4,9 στα 5 αστέρια">
                <span className="text-gold">★</span>
                <span className="text-gold">★</span>
                <span className="text-gold">★</span>
                <span className="text-gold">★</span>
                <span className="relative inline-block text-brown/20">
                  ★
                  <span className="absolute inset-0 w-[90%] overflow-hidden text-gold">★</span>
                </span>
              </div>
              <div className="font-serif text-display-s text-warm-black font-medium leading-none tracking-[-1.5px]">
                4.9 στα 5
              </div>
            </div>
            <p className="max-w-[820px] text-body-l text-muted leading-[1.6]">
              {BUSINESS.reviews.rating} στα 5 από {BUSINESS.reviews.display} πιστοποιημένες αξιολογήσεις στο FRESHA.
            </p>
            <div className="mt-6 lg:hidden">
              <div className="font-serif text-h1 text-warm-black font-medium leading-none">{BUSINESS.reviews.display}</div>
              <div className="mt-2 text-eyebrow uppercase text-brown/70 font-medium">Πιστοποιημένες αξιολογήσεις</div>
            </div>
          </div>
          
          <LazyOnView minHeight={360}><TestimonialsLoop testimonials={TESTIMONIALS} /></LazyOnView>
        </div>
      </section>
      
      {/* 9. INSTAGRAM */}
      <section className="cv-auto px-[6vw] py-[100px]">
        <div className="max-w-container mx-auto">
          <SectionHeader
            eyebrow="Instagram · @ikonomakishair"
            title="Δες περισσότερα"
            titleEm="από το atelier."
            subtitle="Daily transformations, behind the scenes, και inspiration από την ομάδα μας."
          />
          <LazyOnView minHeight={256}>
            <InstagramGrid count={12} />
          </LazyOnView>
        </div>
      </section>
      
      {/* 10. JOURNAL */}
      <section className="cv-auto px-[6vw] py-[100px]">
        <div className="max-w-container mx-auto">
          <SectionHeader
            eyebrow="Atelier Journal"
            title="Insights, tips,"
            titleEm="και η ιστορία μας."
            subtitle="Από care guides μέχρι atelier news. Όλα όσα πρέπει να ξέρεις για το χρώμα, τη φροντίδα και την τέχνη της κομμωτικής."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {JOURNAL.map((j, i) => (
              <JournalCard key={i} {...j} />
            ))}
          </div>
        </div>
      </section>
      
      {/* 11. LOCATION */}
      <section aria-labelledby="location-heading" className="cv-auto bg-warm-black text-cream px-[6vw] py-[120px]">
        <div className="max-w-container-narrow mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <EyebrowPill variant="cream-on-dark" className="mb-7">
                Επισκέψεις · Ηρακλείτου 80
              </EyebrowPill>
              <h2 id="location-heading" className="font-serif text-display-m text-cream font-medium leading-[1] tracking-[-2px] mb-7">
                Στην καρδιά του<br />
                <em className="italic text-gold-light">Χαλανδρίου</em>.
              </h2>
              <p className="text-body-l text-cream/70 leading-[1.6] mb-6">
                Από το atelier μας στην Ηρακλείτου 80, καλωσορίζουμε πελάτισσες από όλο το βόρειο και κεντρικό λεκανοπέδιο.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                {[
                  { icon: '🅿️', text: 'Parking στον δρόμο, κοντά στο atelier' },
                  { icon: '🚌', text: 'Κοντά σε λεωφορείο & μετρό' },
                  { icon: '✦', text: 'Balayage από €150' },
                  { icon: '✦', text: 'Κούρεμα από €45' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 px-4 py-2 rounded-pill bg-gold-light/15 border border-gold-light/25">
                    <span>{item.icon}</span>
                    <span className="text-tiny font-medium text-gold-light">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <AreaPills variant="dark" areas={AREAS} className="mb-10" />
              
              <Button variant="gold-on-dark" href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Κράτηση Online →
              </Button>
            </div>
            
            <div className="relative aspect-square max-h-[520px]">
              <div 
                className="absolute inset-0 rounded-card-xl overflow-hidden"
                style={{ background: 'linear-gradient(165deg, #2a221b 0%, #3a2e22 50%, #1C1814 100%)' }}
              />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gold-bg flex items-center justify-center text-2xl shadow-pin animate-pulse-dot">
                  📍
                </div>
                <div className="bg-cream text-warm-black rounded-pill px-5 py-2 text-tiny font-semibold tracking-wide">
                  Ηρακλείτου 80, Χαλάνδρι
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 12. ABOUT */}
      <section aria-labelledby="about-heading" className="cv-auto px-[6vw] py-[100px] bg-cream-soft">
        <div className="max-w-container-narrow mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <EyebrowPill variant="dark" className="mb-6">Για εμάς</EyebrowPill>
              <h2 id="about-heading" className="font-serif text-display-s text-warm-black font-medium leading-[1] tracking-[-1.5px] mb-6">
                30 χρόνια τέχνης,<br /><em className="italic text-gold">2η γενιά.</em>
              </h2>
              <p className="text-body-l text-muted leading-[1.7] mb-4">
                Το Ikonomakis Hair Atelier είναι premium κομμωτήριο στο Χαλάνδρι με 30+ χρόνια εμπειρίας. Ο ιδρυτής Θοδωρής Οικονομάκης, δεύτερη γενιά κομμωτής, συνδυάζει την κομμωτική τέχνη με σύγχρονη τεχνολογία AI.
              </p>
              <p className="text-body text-muted leading-[1.7]">
                Ως δημιουργός του Kommosis AI και του Face Metrics Pro — των πρώτων AI εργαλείων για κομμωτήρια στην Ελλάδα — έχει εκπαιδευτεί και εκπαιδεύσει σε 19 χώρες. Κάθε υπηρεσία ξεκινά με προσωπομετρία (Face Metrics Pro) και <a href="/prosopologia-visagisme" className="text-gold underline underline-offset-2">προσωπολογία (visagisme)</a>: οι μετρήσεις δείχνουν τι ταιριάζει, η προσωπολογία τι σας εκφράζει.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '30+', label: 'Χρόνια εμπειρίας' },
                { value: BUSINESS.reviews.rating, label: `από ${BUSINESS.reviews.display} κριτικές` },
                { value: '19', label: 'Χώρες εκπαίδευσης' },
                { value: '2η', label: 'Γενιά κομμωτών' },
              ].map((stat) => (
                <div key={stat.label} className="p-6 rounded-card bg-cream border border-brown/10 text-center">
                  <div className="font-serif text-display-s text-gold font-medium leading-none mb-2">{stat.value}</div>
                  <div className="text-tiny text-muted uppercase tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 13. SERVICES DESCRIPTION */}
      <section aria-labelledby="services-desc-heading" className="cv-auto px-[6vw] py-[100px]">
        <div className="max-w-container-narrow mx-auto">
          <EyebrowPill variant="dark" className="mb-6">Υπηρεσίες</EyebrowPill>
          <h2 id="services-desc-heading" className="font-serif text-display-s text-warm-black font-medium leading-[1] tracking-[-1.5px] mb-10">
            Κάθε υπηρεσία,<br /><em className="italic text-gold">σχεδιασμένη για σένα.</em>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { title: 'Balayage — από €150', desc: 'Hand-painted χρώμα με φυσικές μεταβάσεις. Σχεδιάζεται με βάση το σχήμα προσώπου και το lifestyle σου. Αποτέλεσμα που "μεγαλώνει" χάρη για 4–6 μήνες.', href: '/balayage-chalandri' },
              { title: 'Precision Haircut — από €45', desc: 'Ανδρικό και γυναικείο κούρεμα ακριβείας. Κάθε κούρεμα ξεκινά με ανάλυση σχήματος προσώπου για το ιδανικό αποτέλεσμα.', href: '/womens-haircut-chalandri' },
              { title: 'Color Correction', desc: 'Εξειδικευμένη επαναφορά χρώματος μετά από εσφαλμένη βαφή. Διάγνωση, αξιολόγηση κατάστασης μαλλιών και ασφαλής αποκατάσταση.', href: '/color-correction-chalandri' },
              { title: 'Face Metrics Pro — €100 · 45 λεπτά', desc: 'Μορφολογική ανάλυση προσώπου από τον Θοδωρή Οικονομάκη που αναλύει τις αναλογίες του προσώπου για ιδανικό κούρεμα, χρώμα και styling. Πρώτο εργαλείο προσωπομετρίας για κομμωτήρια στην Ελλάδα.', href: '/services/face-metrics-pro' },
            ].map((svc) => (
              <a key={svc.title} href={svc.href} className="block p-6 rounded-card bg-cream-soft border border-brown/10 hover:border-gold/30 transition-colors group">
                <h3 className="font-serif text-h3 text-warm-black font-medium mb-2 group-hover:text-gold transition-colors">{svc.title}</h3>
                <p className="text-body-s text-muted leading-[1.65]">{svc.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 14. FAQ */}
      <section id="faq" aria-labelledby="faq-heading" className="cv-auto px-[6vw] py-[120px] bg-cream-soft scroll-mt-28">
        <div className="max-w-editorial mx-auto">
          <SectionHeader
            eyebrow="Συχνές Ερωτήσεις"
            title="Ό,τι θέλεις"
            titleEm="να μάθεις."
            subtitle="Οι πιο συχνές ερωτήσεις για τις υπηρεσίες, την τοποθεσία και τη μεθοδολογία μας."
            center
          />
          <FAQAccordion items={FAQ_ITEMS} />
        </div>
      </section>
      
      {/* 13. FINAL CTA */}
      <section className="px-[6vw] pb-[120px]">
        <FinalCTACard
          title="Έτοιμη να ζήσεις"
          titleEm="τη διαφορά;"
          description="Κλείσε το ραντεβού σου online σε 60 δευτερόλεπτα. 3 tiers ειδικοτήτων, ευέλικτες ώρες, secure online booking."
          ctaText="Κράτηση Online →"
          ctaHref={BOOKING_URL}
          ctaTarget="_blank"
          ctaRel="noopener noreferrer"
        />
      </section>
      <FindUsSection />
      </main>
      <Footer />
    </div>
  );
}